import { useState, useCallback, useEffect } from 'react';

import type { ViewState } from '../shared/types';

interface RotationControls {
  isRotatingLeft: boolean;
  isRotatingRight: boolean;
  handleRotateLeftMouseDown: () => void;
  handleRotateRightMouseDown: () => void;
  handleRotateMouseUp: () => void;
  resetRotation: () => void;
}

interface UseRotationProps {
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
}

export const useRotation = ({ setViewState }: UseRotationProps): RotationControls => {
  const [isRotatingLeft, setIsRotatingLeft] = useState(false);
  const [isRotatingRight, setIsRotatingRight] = useState(false);

  // Continuous rotation effect
  useEffect(() => {
    if (!isRotatingLeft && !isRotatingRight) return;

    const rotationSpeed = 0.02; // radians per frame (~1.15 degrees per frame at 60fps)
    let animationFrameId: number;

    const rotate = () => {
      const delta = isRotatingLeft ? rotationSpeed : -rotationSpeed;
      setViewState(prev => ({ ...prev, rotation: prev.rotation + delta }));
      animationFrameId = requestAnimationFrame(rotate);
    };

    animationFrameId = requestAnimationFrame(rotate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isRotatingLeft, isRotatingRight, setViewState]);

  const resetRotation = useCallback(() => {
    setViewState(prev => ({
      ...prev,
      rotation: 0
    }));
  }, [setViewState]);

  const handleRotateLeftMouseDown = useCallback(() => {
    setIsRotatingLeft(true);
  }, []);

  const handleRotateRightMouseDown = useCallback(() => {
    setIsRotatingRight(true);
  }, []);

  const handleRotateMouseUp = useCallback(() => {
    setIsRotatingLeft(false);
    setIsRotatingRight(false);
  }, []);

  return {
    isRotatingLeft,
    isRotatingRight,
    handleRotateLeftMouseDown,
    handleRotateRightMouseDown,
    handleRotateMouseUp,
    resetRotation
  };
};
